import { useState, useEffect } from 'react';
import { authService, userProfileService, type UserProfile } from '../lib/auth';
import type { User, Session, AuthError } from '@supabase/supabase-js';

interface UseAuthReturn {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ user: User | null; error: AuthError | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ user: User | null; error: AuthError | null }>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ profile: UserProfile | null; error: Error | null }>;
  createProfile: (fullName: string) => Promise<{ profile: UserProfile | null; error: Error | null }>;
  isAuthenticated: boolean;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    // Listen for auth changes - this handles both initial state and updates
    const { data: { subscription } } = authService.onAuthStateChange(async (newUser, newSession) => {
      if (!isMounted) return;

      setUser(newUser);
      setSession(newSession);

      if (newUser) {
        try {
          const { profile } = await userProfileService.getProfile(newUser.id);
          if (isMounted) setProfile(profile);
        } catch {
          if (isMounted) setProfile(null);
        }
      } else {
        setProfile(null);
      }

      if (isMounted) setLoading(false);
    });

    // Also fetch initial session explicitly (for page refresh scenarios)
    const initializeAuth = async () => {
      try {
        const { user: currentUser, session: currentSession } = await authService.getCurrentUser();
        if (!isMounted) return;

        setUser(currentUser);
        setSession(currentSession);

        if (currentUser) {
          const { profile } = await userProfileService.getProfile(currentUser.id);
          if (isMounted) setProfile(profile);
        }
      } catch {
        // Silent fail - user not logged in
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string): Promise<{ user: User | null; error: AuthError | null }> => {
    const { user, error } = await authService.signIn(email, password);
    return { user, error };
  };

  const signUp = async (email: string, password: string, fullName?: string): Promise<{ user: User | null; error: AuthError | null }> => {
    const { user, error } = await authService.signUp(email, password, fullName);
    return { user, error };
  };

  const signInWithGoogle = async (): Promise<{ error: AuthError | null }> => {
    const { error } = await authService.signInWithGoogle();
    return { error };
  };

  const signOut = async (): Promise<{ error: AuthError | null }> => {
    const { error } = await authService.signOut();
    if (!error) {
      setUser(null);
      setSession(null);
      setProfile(null);
    }
    return { error };
  };

  const resetPassword = async (email: string): Promise<{ error: AuthError | null }> => {
    const { error } = await authService.resetPassword(email);
    return { error };
  };

  const updateProfile = async (updates: Partial<UserProfile>): Promise<{ profile: UserProfile | null; error: Error | null }> => {
    if (!user) return { profile: null, error: new Error('No user logged in') };
    
    const { profile: updatedProfile, error } = await userProfileService.updateProfile(user.id, updates);
    if (!error && updatedProfile) {
      setProfile(updatedProfile);
    }
    return { profile: updatedProfile, error };
  };

  const createProfile = async (fullName: string): Promise<{ profile: UserProfile | null; error: Error | null }> => {
    if (!user) return { profile: null, error: new Error('No user logged in') };
    
    const { profile: newProfile, error } = await userProfileService.createProfile(user.id, fullName);
    if (!error && newProfile) {
      setProfile(newProfile);
    }
    return { profile: newProfile, error };
  };

  return {
    user,
    session,
    profile,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resetPassword,
    updateProfile,
    createProfile,
    isAuthenticated: !!user
  };
}
